/**
 * Header Row Styles
 *
 * Styles for the grid header including column headers, sort indicators,
 * resize handles, and column groups.
 *
 * @layer tbw-base
 */

@layer tbw-base {
  tbw-grid {
    .header {
      display: block;
      flex-shrink: 0;
      z-index: var(--tbw-z-layer-header, 30);
      background: var(--tbw-color-header-bg);
      overflow: visible;
    }

    .header-group-row {
      display: grid;
      grid-template-columns: var(--tbw-column-template);
      background: var(--tbw-color-header-bg);
      z-index: var(--tbw-z-layer-header, 30);
    }

    .header-group-cell {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: var(--tbw-cell-padding-header, 2px 8px);
      color: var(--tbw-color-header-group-fg, var(--tbw-color-header-fg));
      font-weight: var(--tbw-font-weight-header-group, var(--tbw-font-weight-header));
      justify-content: var(--tbw-align-header-group, var(--tbw-align-header, flex-start));

      &:not(:last-child) {
        border-right: 2px solid var(--tbw-color-border);
      }
    }

    .header-row {
      display: grid;
      grid-template-columns: var(--tbw-column-template);
      color: var(--tbw-color-header-fg);
      font-size: var(--tbw-font-size-header);
      min-height: var(--tbw-header-height);
      border-bottom: var(--tbw-border-header);
      z-index: var(--tbw-z-layer-header, 30);
      text-transform: var(--tbw-header-text-transform);
      letter-spacing: var(--tbw-header-letter-spacing);

      > .cell {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: var(--tbw-cell-padding-header, 2px 8px);
        background-color: var(--tbw-color-header-bg);
        font-weight: var(--tbw-font-weight-header);
        border-right: 1px solid var(--tbw-color-border-cell);
        /* overflow: visible allows resize handles to extend past cell boundary */
        overflow: visible;
        min-width: 0;

        > span:first-child {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: inherit;
        }

        > span[part~='sort-indicator'] {
          flex-shrink: 0;
          opacity: 0.6;
          color: var(--tbw-sort-indicator-color);
          display: var(--tbw-sort-indicator-display, inline-flex);
          visibility: var(--tbw-sort-indicator-visibility, visible);
          transition:
            opacity 0.15s,
            visibility 0s,
            display 0s allow-discrete;
          transition-behavior: allow-discrete;
        }

        /* Show sort indicator on hover or when actively sorted */
        &:hover > span[part~='sort-indicator'] {
          display: inline-flex;
          visibility: visible;
        }

        &[aria-sort='ascending'] > span[part~='sort-indicator'],
        &[aria-sort='descending'] > span[part~='sort-indicator'] {
          display: inline-flex;
          visibility: visible;
          opacity: 1;
          color: var(--tbw-sort-indicator-active-color);
        }

        &:last-child {
          border-right: 0;

          /* The last column has no neighbour to bleed into, so the handle is
             pulled fully inside the cell instead of overflowing it. Keep the
             full target width rather than clipping to half — a 9px-wide handle
             fails SC 2.5.8, and it is the one handle with no adjacent column
             header to fall back on for spacing. */
          .resize-handle {
            right: 0;
            width: max(var(--tbw-resize-hit-area, 18px), var(--tbw-touch-target-min, 24px));

            /* Hit area grew inward; pin the visible bar back to the cell edge
               so it still reads as the column boundary. */
            &::before {
              left: auto;
              right: 0;
              transform: none;
            }
          }

          /* Matching reservation for the now full-width handle (see below). */
          &.resizable {
            padding-inline-end: max(var(--tbw-cell-padding-h, 0.5em), var(--tbw-touch-target-min, 24px));
          }
        }

        &.grouped.group-end:not(:last-child) {
          border-right: 2px solid var(--tbw-color-border);
        }

        &.resizable {
          position: relative;
          /* The resize handle's target reaches half its width into the cell.
             Keep the trailing controls clear of it, or they would lose part of
             their own target to it (SC 2.5.8). */
          padding-inline-end: max(var(--tbw-cell-padding-h, 0.5em), calc(var(--tbw-touch-target-min, 24px) / 2));
        }

        &.sticky-left,
        &.sticky-right {
          background: var(--tbw-color-header-bg);
          z-index: 35;
        }
      }
    }

    .header-group-row > .header-group-cell {
      &.sticky-left,
      &.sticky-right {
        background: var(--tbw-color-header-bg);
        z-index: 35;
      }
    }

    /* ========== Sorting & Resize ========== */

    .sortable {
      cursor: pointer;
      user-select: none;
    }

    .resize-handle {
      position: absolute;
      top: 0;
      /* Transparent hit area, never narrower than the 24px SC 2.5.8 target.
         Absolutely positioned, so widening it costs no layout. */
      right: calc(max(var(--tbw-resize-hit-area, 18px), var(--tbw-touch-target-min, 24px)) / -2);
      width: max(var(--tbw-resize-hit-area, 18px), var(--tbw-touch-target-min, 24px));
      height: 100%;
      cursor: e-resize;
      user-select: none;
      touch-action: none;
      z-index: 20;
      background: transparent;

      /* Visible handle bar rendered at --tbw-resize-handle-width */
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: var(--tbw-resize-handle-width);
        height: 100%;
        background: var(--tbw-resize-handle-color);
        border-radius: var(--tbw-resize-handle-border-radius);
        transition: background 120ms ease;
      }

      /* Vertical line indicator extending through all rows on hover */
      &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: var(--tbw-resize-handle-width);
        height: 0;
        background: var(--tbw-resize-indicator-color, var(--tbw-color-accent));
        opacity: 0;
        pointer-events: none;
        transition:
          opacity 120ms ease,
          height 0s 120ms;
        z-index: 1000;
      }

      /* `[data-tbw-size-open]` keeps the indicator up while the size popover
         is open, so the popover stays visually tied to its border without
         having to follow it. */
      &:hover,
      &[data-tbw-size-open] {
        &::before {
          background: var(--tbw-resize-handle-color-hover);
        }

        &::after {
          /* Extend to full grid height - uses viewport height as approximation */
          height: 100vh;
          opacity: var(--tbw-resize-indicator-opacity, 0.6);
          transition:
            opacity 120ms ease,
            height 0s;
        }
      }
    }

    /* Coarse pointer (touch): ensure the sort indicator meets the minimum
       touch-target size. The resize handle is already sized for it above. */
    @media (pointer: coarse) {
      > .cell.sortable > span[part~='sort-indicator'] {
        min-width: var(--tbw-touch-target-min, 24px);
        min-height: var(--tbw-touch-target-min, 24px);
      }
    }
  }
}
